import {
  BookOpenText,
  Buildings,
  ChartLineUp,
  ClockCounterClockwise,
  Code,
  Database,
  Handshake,
  HardDrives,
  type Icon,
  Lightning,
  Medal,
  Rocket,
  SlidersHorizontal,
  Terminal,
  Users,
} from "@phosphor-icons/react"
import { COVERAGE } from "@tokenterminal/core/data/coverage"

export interface InfrastructureItem {
  heading: string
  content: string
  icon: Icon
}

export interface EdgeItem {
  heading: string
  content: string
  icon: Icon
}

export interface AudienceItem {
  label: string
  icon: Icon
}

export const heroContent = {
  headline:
    "Token Terminal transforms raw blockchain data into institutional-grade intelligence",
  subheadline: "The foundational layer for onchain data",
  description:
    "Token Terminal is a full-stack onchain data platform that powers institutions, data & research outlets, and developers. We operate the most advanced data infrastructure in crypto – a vertically integrated gigafactory that ingests, decodes, standardizes, and serves petabytes of blockchain data with the highest quality.",
  secondaryDescription:
    "In a market built on transparency, we believe the infrastructure behind crypto must reflect the same principles: open, auditable, and uncompromisingly reliable.",
  tagline:
    "We go all the way to the source – and make onchain data human-readable, comparable, and actionable.",
}

export const whyWeExist = {
  heading: "Why we exist",
  problem:
    "Blockchains are public databases, yet the industry has relied on aggregators that collect data from inconsistent third-party sources.",
  consequence:
    "This breaks the core promise of crypto: openly accessible, auditable data.",
  solution:
    "Token Terminal replaces this fragmented model with full-stack control over the entire data supply chain:",
  capabilities: [
    "Direct ingestion of raw blockchain data",
    "Decoding of smart contract activity",
    "Standardization into comparable financial and business metrics",
    "Delivery through high-performance APIs and analytical surfaces",
  ],
  result: `With complete ownership and zero platform dependencies, we ensure accuracy, consistency, and auditability across ${COVERAGE.chains} chains and thousands of applications and tokenized assets.`,
}

export const infrastructure: InfrastructureItem[] = [
  {
    heading: "Direct sourcing from nodes",
    content:
      "We ingest raw blocks, logs, traces, and events directly from RPC nodes to ensure independence, data ownership, and complete historical coverage.",
    icon: Database,
  },
  {
    heading: "Full backfill with entire history",
    content:
      "We capture the entire data history from genesis to eliminate re-indexing and reproducibility.",
    icon: ClockCounterClockwise,
  },
  {
    heading: "A purpose-built data warehouse",
    content:
      "All raw data is stored and structured in BigQuery, which we've optimized for large-scale query performance and cross-chain analysis.",
    icon: HardDrives,
  },
  {
    heading: "Decoding smart contracts at scale",
    content:
      "We transform smart contract activity into clean, interpretable event/function datasets across thousands of protocols.",
    icon: Code,
  },
  {
    heading: "Financial abstractions and standardized KPIs",
    content:
      "We convert decoded data into consistent, GAAP-like metrics for usage and financial performance.",
    icon: ChartLineUp,
  },
  {
    heading: "Serving via high-performance systems",
    content:
      "BigQuery and ClickHouse power our API, web platform, and third-party integrations – all from a unified pipeline.",
    icon: Lightning,
  },
]

export const infrastructureIntro = {
  heading: "Our infrastructure",
  description:
    "Over years of R&D, we've built a modular, chain-agnostic infrastructure capable of operating at petabyte scale – something only a handful of companies in the world can do.",
  tagline:
    "This infrastructure is our strategic moat; a gigafactory for onchain data.",
}

export const institutionalStandard = {
  heading: "The institutional standard",
  description:
    "Token Terminal's data underpins decision-making for leading financial institutions, index providers, hedge funds, and developers of leading blockchains and applications.",
  useCases:
    "We support use cases ranging from regulatory-grade disclosures and portfolio analytics to risk systems, analytics dashboards, and direct integrations to third-party data platforms.",
  positioning:
    "By maintaining the entire data-supply chain inhouse, from transforming raw blockchain data into standardized, comparable metrics, we are uniquely positioned to define the accounting standard for the crypto market.",
}

export const ourEdge: EdgeItem[] = [
  {
    heading: "Data ownership",
    content:
      "We own the full pipeline, from ingestion to interpretation. This eliminates dependence on external APIs.",
    icon: Handshake,
  },
  {
    heading: "End-to-end control",
    content:
      "We can update models, fix issues, and iterate instantly – something that is impossible for aggregators.",
    icon: SlidersHorizontal,
  },
  {
    heading: "Superior quality and standardization",
    content:
      "Our methodological rigor ensures accuracy and comparability across chains, market sectors, and projects.",
    icon: Medal,
  },
  {
    heading: "Scalability",
    content:
      "Modular playbooks, automation, and template-driven integrations allow us to onboard new blockchains and projects in days, not weeks.",
    icon: Rocket,
  },
]

export const edgeConclusion =
  "Together, these elements create a defensible position that grows stronger as the industry scales."

export const whoWeServe: AudienceItem[] = [
  { label: "Institutional investors", icon: Buildings },
  { label: "Research and data platforms", icon: BookOpenText },
  { label: "Protocol teams and foundations", icon: Users },
  {
    label: "Developers and analysts seeking raw-to-standardized data access",
    icon: Terminal,
  },
]

export const whatWeBelieve = {
  heading: "What we believe",
  intro:
    "Blockchains made financial data open to the world. We make that data usable.",
  beliefs: [
    "blockchain data should be sourced directly from the ledger",
    "true transparency requires full-stack infrastructure",
    "standardized, auditable data is essential for institutional adoption",
    "the future of crypto markets depends on reliable, interpretable onchain data",
  ],
  conclusion: "Token Terminal is building the intelligence layer for crypto.",
}

export const whoWeAre = {
  heading: "Who we are",
  paragraphs: [
    "Token Terminal is a 25-person, deeply technical team of senior developers, data engineers, and researchers dedicated to building the industry-standard platform for onchain data. We operate with the efficiency, rigor, and focus of a world-class engineering organization — small by design, but with an outsized impact on the crypto analytics landscape.",
    "Our team brings experience from leading technology companies and high-performing environments, including Google, Vercel, Amazon, Accenture, and top-tier consulting and law firms. This diversity of backgrounds strengthens our ability to solve one of the most complex data problems in the world: making blockchain data standardized, interpretable, and institution-ready.",
    "We are a meritocratic, product-first organization. The best ideas win, regardless of position. We maintain a flat hierarchy, encourage ownership at every level, and expect clear, pragmatic communication. Our culture is output-driven, built around autonomy, accountability, and a shared commitment to excellence.",
    "We care deeply about building the winning data platform for crypto. Every person on the team has a direct impact on the product, the infrastructure, and the future of institutional onchain analytics.",
  ],
}

export const stats = [
  {
    heading: "We ingest",
    stat: "300,000,000+",
    subheading: "blockchain transactions per day",
  },
  {
    heading: "We decode",
    stat: "5,000+",
    subheading: "smart contracts every month.",
  },
  {
    heading: "We manage",
    stat: "300TB+",
    subheading: "of raw blockchain data.",
  },
  {
    heading: "We maintain",
    stat: "11,000,000+",
    subheading: "standardized data points.",
  },
]
